import React, {
    FC,
    useEffect,
    useState,
} from "react"
import {
    Alert,
    Badge,
    Card,
    Col,
    Container,
    Row,
} from "react-bootstrap"
import axios from "axios"
import {useTranslation} from "react-i18next"

import {useAuth} from "../../../context/AuthProvider"
import TenantTree from "../../exam-management/components/TenantTree"
import {
    getTenantLabel,
    TenantTreeNode,
} from "../../exam-management/components/tenant.types"

type TenantTreeComponentProps =
    Record<string, unknown>

const TenantTreeComponent: FC<
    TenantTreeComponentProps
> = () => {
    const auth = useAuth()
    const {t} = useTranslation("admin")

    const [tree, setTree] =
        useState<TenantTreeNode[]>([])
    const [selected, setSelected] =
        useState<TenantTreeNode | null>(null)
    const [loading, setLoading] =
        useState<boolean>(true)
    const [error, setError] =
        useState<boolean>(false)

    const isAdmin = auth.hasGate("admin")

    useEffect(() => {
        if (!isAdmin) {
            setLoading(false)
            return
        }

        axios
            .get(
                "/api/tenant/tree",
                auth.axiosAuthHeader()
            )
            .then(response => {
                setTree(
                    Array.isArray(response.data)
                        ? response.data
                        : []
                )
                setError(false)
            })
            .catch(() => {
                setError(true)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [isAdmin])

    if (!isAdmin) {
        return (
            <Container fluid>
                <Alert variant="danger">
                    {t("tenantDebug.adminOnly")}
                </Alert>
            </Container>
        )
    }

    return (
        <div className="component">
            <Container fluid>
                <Card>
                    <Card.Header>
                        <h3 className="mb-0">
                            <i className="bi bi-diagram-3 me-2"/>
                            {t("tenantDebug.title")}
                        </h3>
                    </Card.Header>

                    <Card.Body>
                        {loading && (
                            <Alert variant="info">
                                {t("tenantDebug.loading")}
                            </Alert>
                        )}

                        {error && (
                            <Alert variant="danger">
                                {t("tenantDebug.loadError")}
                            </Alert>
                        )}

                        {!loading && !error && (
                            <Row className="g-3">
                                <Col
                                    xs={12}
                                    xl={8}
                                >
                                    <TenantTree
                                        nodes={tree}
                                        selectedTenantId={
                                            selected?._id
                                        }
                                        onSelect={setSelected}
                                    />
                                </Col>

                                <Col
                                    xs={12}
                                    xl={4}
                                >
                                    <Card>
                                        <Card.Header>
                                            {t("tenantDebug.details")}
                                        </Card.Header>

                                        <Card.Body>
                                            {!selected ? (
                                                <Alert variant="info">
                                                    {t("tenantDebug.select")}
                                                </Alert>
                                            ) : (
                                                <>
                                                    <h4>
                                                        {getTenantLabel(
                                                            selected
                                                        )}
                                                    </h4>

                                                    <dl>
                                                        <dt>ID</dt>
                                                        <dd>
                                                            <code>
                                                                {selected._id}
                                                            </code>
                                                        </dd>

                                                        <dt>Describer</dt>
                                                        <dd>
                                                            {selected.describer}
                                                        </dd>

                                                        <dt>Source UUID</dt>
                                                        <dd>
                                                            {selected.sourceUuid}
                                                        </dd>

                                                        <dt>Parent</dt>
                                                        <dd>
                                                            {selected.parent ||
                                                            "Kein Parent"}
                                                        </dd>

                                                        <dt>{t("tenantDebug.children")}</dt>
                                                        <dd>
                                                            {selected.children.length}
                                                        </dd>

                                                        <dt>{t("tenantDebug.flags")}</dt>
                                                        <dd className="d-flex flex-wrap gap-1">
                                                            {selected.isDepartment && (
                                                                <Badge bg="primary">
                                                                    {t("tenantDebug.department")}
                                                                </Badge>
                                                            )}

                                                            {selected.isDean && (
                                                                <Badge bg="secondary">
                                                                    {t("tenantDebug.dean")}
                                                                </Badge>
                                                            )}

                                                            {selected.isInfrastructure && (
                                                                <Badge bg="dark">
                                                                    {t("tenantDebug.infrastructure")}
                                                                </Badge>
                                                            )}

                                                            {!selected.isDepartment &&
                                                            !selected.isDean &&
                                                            !selected.isInfrastructure && (
                                                                <Badge bg="light" text="dark">
                                                                    {t("tenantDebug.noFlags")}
                                                                </Badge>
                                                            )}
                                                        </dd>
                                                    </dl>
                                                </>
                                            )}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </div>
    )
}

export default TenantTreeComponent
